<template>
    <AppModal ref="filtersModal" :title="translate('labels.table_filters')">
        <div class="modal-body">
           <table class="table table-bordered table-fixed">
                <thead>
                    <tr>
                        <th width="50%">{{ $t('labels.field') }}</th>
                        <th width="50%">{{ $t('labels.action') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="field in fields">
                        <tr>
                            <td>{{ field.label }}</td>
                            <td>
                                <AppSelect
                                    :options="getActionOptionsByField(field)"
                                    :model-value="field.selectedAction?.key"
                                    :selected-value="field.selectedAction?.label"
                                    @update:model-value="selectAction(field, $event)"
                                    option-key="key"
                                    option-value="label"
                                    can-be-empty
                                />
                            </td>
                        </tr>
                        <tr v-if="field.selectedAction && (!['present', 'missing'].includes(field.selectedAction.key) || field.key === 'tag')">
                            <td v-if="['export_enabled'].includes(field.key)" colspan="2">
                                <AppCheckbox
                                    v-model="field.value"
                                />
                            </td>
                            <td v-else-if="['tag'].includes(field.key)" colspan="2">
                                <MultiAutoComplete
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    url="/integration-product-tags"
                                />
                            </td>
                            <td v-else-if="field.key === 'manufacturer'" colspan="2">
                                <div class="row">
                                    <div class="col-md-6">
                                        <AppSelect
                                            :options="[
                                                { key: 'contains', label: $t('labels.partial')},
                                                { key: 'starts_with', label: $t('labels.from_the_beginning')},
                                                { key: 'ends_with', label: $t('labels.from_the_end')}
                                            ]"
                                            :model-value="field.value?.param?.key"
                                            :selected-value="field.value?.param?.label"
                                            @update:model-value="field.value.param = $event"
                                            option-key="key"
                                            option-value="label"
                                        />
                                    </div>
                                    <div class="col-md-6">
                                        <Input
                                            v-model="field.value.string"
                                        />
                                    </div>
                                </div>
                            </td>
                            <td v-else-if="['name', 'description'].includes(field.key)" colspan="2">
                                <div class="row">
                                    <div class="col-md-6">
                                        <AppSelect
                                            :options="[
                                                { key: 'contains', label: $t('labels.partial')},
                                                { key: 'starts_with', label: $t('labels.from_the_beginning')},
                                                { key: 'ends_with', label: $t('labels.from_the_end')}
                                            ]"
                                            :model-value="field.value?.param?.key"
                                            :selected-value="field.value?.param?.label"
                                            @update:model-value="field.value.param = $event"
                                            option-key="key"
                                            option-value="label"
                                        />
                                    </div>
                                    <div class="col-md-6">
                                        <Input
                                            v-model="field.value.string"
                                        />
                                    </div>
                                </div>
                            </td>
                            <template v-else-if="['created_at'].includes(field.key)">
                                <td v-if="field.selectedAction.key === 'from'" colspan="2">
                                    <DatePicker
                                        v-model="field.value.from"
                                        :with-time="true"
                                    />
                                </td>
                                <td v-else-if="field.selectedAction.key === 'to'" colspan="2">
                                    <DatePicker
                                        v-model="field.value.to"
                                        :with-time="true"
                                    />
                                </td>
                                <td colspan="2" v-else-if="field.selectedAction.key === 'range'">
                                    <div class="row align-items-center">
                                        <div class="col-lg-5">
                                            <DatePicker
                                                v-model="field.value.from"
                                                :with-time="true"
                                            />
                                        </div>
                                        <div class="col-lg-2 text-center">
                                            {{ $t('labels.to') }}
                                        </div>
                                        <div class="col-lg-5">
                                            <DatePicker
                                                v-model="field.value.to"
                                                :with-time="true"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </template>
                            <template v-else-if="['balance'].includes(field.key)">
                                <td v-if="field.selectedAction.key === 'range'" colspan="2">
                                    <div class="row align-items-center">
                                        <div class="col-lg-5">
                                            <Input
                                                v-model="field.value.balance_from"
                                                type="number"
                                                :placeholder="$t('labels.from')"
                                            />
                                        </div>
                                        <div class="col-lg-2 text-center">
                                            {{ $t('labels.to') }}
                                        </div>
                                        <div class="col-lg-5">
                                            <Input
                                                v-model="field.value.balance_to"
                                                type="number"
                                                :placeholder="$t('labels.to')"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </template>
                            <td v-else-if="field.is_attribute" colspan="2">
                                <MultiAutoComplete
                                    v-if="field.selectedAction?.key === 'correlate'"
                                    :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filter-items`"
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    :query-params="{
                                        integration_filter_uuids: [field.uuid],
                                        orders: [{column: `names->${mainIntegrationLanguage}`, dir: 'asc'}]
                                    }"
                                />
                             </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <div class="modal-footer">
            <AppButton size="sm" @click="apply" color="teal">{{ translate('buttons.apply') }}</AppButton>
        </div>
    </AppModal>
</template>
<script>
import FormGroup from "../../../../../../components/new/form/group/FormGroup.vue";
import AppSelect from '../../../../../../components/new/form/select/AppSelect.vue';
import MultiAutoComplete from "../../../../../../components/new/form/autocomplete/MultiAutoComplete.vue";
import AppCheckbox from '../../../../../../components/new/form/checkbox/AppCheckbox.vue';
import Input from '../../../../../../components/new/form/Input.vue';
import DatePicker from '../../../../../../components/new/form/datePiker/DatePicker.vue';
import AppModal from "../../../../../../components/new/modal/AppModal.vue";
import AppButton from "../../../../../../components/new/button/AppButton.vue";

export default {
    name: "CategoryProductsFiltersModal",
    components: {
        FormGroup, AppSelect, MultiAutoComplete, AppCheckbox, Input, DatePicker, AppModal, AppButton
    },
    emits: ['update:filters'],
    props: {
        filters: {
            type: Array,
            required: true
        },
        integration: {
            type: Object,
            required: true
        },
        mainIntegrationLanguage: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            fields: []
        }
    },
    methods: {
        clear() {
            this.fields.forEach(field => {
                field.selectedAction = null;
                field.value = null;
            });
            this.$emit('update:filters', {}, 0);
        },
        show() {
            if (this.fields.length === 0) {
                const standardFields = [
                    { key: 'export_enabled', label: this.$t('labels.exportEnabled'), selectedAction: null, value: null },
                    { key: 'name', label: this.$t('table.titles.name'), selectedAction: null, value: null },
                    { key: 'description', label: this.$t('table.titles.description'), selectedAction: null, value: null },
                    { key: 'tag', label: this.$t('labels.tags'), selectedAction: null, value: null},
                    { key: 'created_at', label: this.$t('table.titles.created_at'), selectedAction: null, value: null },
                    { key: 'balance', label: this.$t('table.titles.balance'), selectedAction: null, value: null },
                    { key: 'manufacturer', label: this.$t('labels.manufacturer'), selectedAction: null, value: null }
                ];

                const attributeFields = this.filters.map(filter => {
                    let label = filter.name;

                    if (filter.measurement_unit) {
                        label += ` (${filter.measurement_unit})`;
                    }

                    return {
                        key: `attribute_${filter.uuid}`,
                        label: label,
                        is_attribute: true,
                        uuid: filter.uuid,
                        selectedAction: null,
                        value: null
                    };
                });

                this.fields = [...standardFields, ...attributeFields];
            }
            
            this.$refs.filtersModal.show();
        },
        getActionOptionsByField(field) {
            let options = [];

            if (field.is_attribute) {
                return [
                    { key: 'correlate', label: this.$t('labels.correlate') },
                    { key: 'missing', label: this.$t('labels.missing') },
                ];
            }

            const key = field.key;

            if (key === 'tag') {
                options = [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                ];
            }

            if (key === 'export_enabled') {
                options = [
                    { key: 'correlate', label: this.$t('labels.correlate') },
                ]
            }

            if (key === 'manufacturer') {
                options = [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                    { key: 'coincidence', label: this.$t('labels.coincidence') },
                ]
            }

            if (key === 'created_at') {
                options = [
                    { key: 'from', label: this.$t('labels.from') },
                    { key: 'to', label: this.$t('labels.to') },
                    { key: 'range', label: this.$t('labels.range') },
                ]
            }

            if (key === 'name' || key === 'description') {
                options = [
                    { key: 'coincidence', label: this.$t('labels.coincidence') },
                ]
            }

            if (key === 'balance') {
                options = [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                    { key: 'range', label: this.$t('labels.range') },
                ]
            }

            return options;
        },
        selectAction(field, action) {
            if (field.is_attribute) {
                field.value = action ? [] : null;
                field.selectedAction = action;
                return;
            }

            if (action?.key === 'coincidence') {
                field.value = { param: { key: 'contains', label: this.$t('labels.partial')}, string: null };
            } else if (field.key === 'created_at') {
                if (action?.key === 'from') {
                    field.value = { from: null };
                } else if (action?.key === 'to') {
                    field.value = { to: null };
                } else if (action?.key === 'range') {
                    field.value = { from: null, to: null };
                }
            } else if (field.key === 'balance') {
                if (action?.key === 'present') {
                    field.value = {
                        balance_from: 1
                    }
                } else if (action?.key === 'missing') {
                    field.value = {
                        balance_to: 0
                    }
                } else if (action?.key === 'range') {
                    field.value = {
                        balance_from: null,
                        balance_to: null
                    }
                }
            } else {
                field.value = null;
            }

            field.selectedAction = action;
        },
        applyFilters() {
             return this.buildFilterRequest();
        },
        apply() {
            this.$emit('update:filters', this.applyFilters(), this.fields.filter(f => f.selectedAction).length)
            this.$refs.filtersModal.hide()
        },
        getFilters() {
            return this.applyFilters();
        },
        buildFilterRequest() {
            let filterRequest = {};
            const selectedFilters = this.fields.filter(field => field.selectedAction);

            let attributeFilters = [];

            for (let field of selectedFilters) {
                if (field.is_attribute) {
                    if (field.selectedAction.key === 'missing') {
                        attributeFilters.push({
                            filter_uuid: field.uuid,
                            filter_item_uuid: null
                        });
                    } else if (field.value && field.value.length) {
                        for (const filterItem of field.value) {
                            attributeFilters.push({
                                filter_uuid: field.uuid,
                                filter_item_uuid: filterItem.uuid
                            });
                        }
                    }
                    continue;
                }

                const action = field.selectedAction.key;

                if (field.key === 'name' || field.key === 'description') {
                    if (action === 'coincidence') {
                        filterRequest[`${field.key}_search_type`] = field.value?.param?.key;
                        filterRequest[`${field.key}_value`] = field.value?.string ?? null;
                    }

                    continue;
                }

                if (field.key === 'export_enabled') {
                    filterRequest['export_enabled'] = field.value !== null ? Number(field.value) : 0;
                    continue;
                }

                if (field.key === 'tag') {
                    const tagUuids = field.value !== null ? field.value.map(tag => tag.uuid) : [];
                    filterRequest[action === 'present' ? 'exists_tag_uuids' : 'not_exists_tag_uuids'] = tagUuids;
                    continue;
                }

                if (field.key === 'created_at') {
                    if (field.value?.from) {
                        filterRequest['created_at_from'] = field.value.from;
                    }
                    if (field.value?.to) {
                        filterRequest['created_at_to'] = field.value.to;
                    }
                    continue;
                }

                if (field.key === 'balance') {
                    if (field.value?.balance_from !== undefined && field.value?.balance_from !== null) {
                        filterRequest['balance_from'] = field.value.balance_from;
                    }
                    if (field.value?.balance_to !== undefined && field.value?.balance_to !== null) {
                        filterRequest['balance_to'] = field.value.balance_to;
                    }
                    continue;
                }

                if (field.key === 'manufacturer') {
                    if (action === 'present') {
                        filterRequest['manufacturer_is_filled'] = 1;
                    } else if (action === 'missing') {
                        filterRequest['manufacturer_is_filled'] = 0;
                    } else if (action === 'coincidence') {
                        filterRequest['manufacturer_search_type'] = field.value?.param?.key;
                        filterRequest['manufacturer_value'] = field.value?.string ?? null;
                    }
                    continue;
                }
            }

            if (attributeFilters.length) {
                filterRequest['filters'] = attributeFilters;
            }

            return filterRequest;
        }
    }
}
</script>
